import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  Stack,
  Autocomplete,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const engineerLabel = (engineer) => {
  if (!engineer) return '';
  if (typeof engineer === 'string') return engineer;
  return `${engineer.firstName || ''} ${engineer.lastName || ''}`.trim() || engineer.email || engineer._id;
};

const idOf = (value) => (value && typeof value === 'object' ? String(value._id || '') : String(value || ''));

const checkLabel = (check) => {
  const seq = (check?.sequenceLabel && String(check.sequenceLabel).trim())
    ? check.sequenceLabel
    : check?.sequenceNumber;
  const desc = check?.description || 'Checklist item';
  return `S/N ${seq}: ${desc}`;
};

/**
 * Hand over active shift ownership.
 * Free: any MRO engineer (searchable), or release to open pool without assigning.
 * Locked: only manager-assigned team when mroEngineerTeamLocked is true (release still allowed).
 * When ampChecks exist, incomplete checks are shown and included with the handover note.
 */
const MROHandoverDialog = ({ open, onClose, workOrder, mroEngineers: engineersProp, onSuccess, useFacade = false }) => {
  const [handoverMode, setHandoverMode] = useState('ASSIGN'); // ASSIGN | RELEASE
  const [selectedEngineer, setSelectedEngineer] = useState(null);
  const [notes, setNotes] = useState('');
  const [selectedCheckIds, setSelectedCheckIds] = useState([]);
  const [mroEngineers, setMroEngineers] = useState(engineersProp || []);
  const [engineersLoading, setEngineersLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  const incompleteChecks = useMemo(() => {
    if (!Array.isArray(workOrder?.ampChecks)) return [];
    return workOrder.ampChecks.filter((check) => check && check.status !== 'COMPLETED' && check._id);
  }, [workOrder]);

  const hasChecks = Array.isArray(workOrder?.ampChecks) && workOrder.ampChecks.length > 0;

  useEffect(() => {
    if (Array.isArray(engineersProp) && engineersProp.length) {
      setMroEngineers(engineersProp);
    }
  }, [engineersProp]);

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    (async () => {
      setEngineersLoading(true);
      try {
        const response = await axios.get('/api/users');
        const users = Array.isArray(response.data) ? response.data : [];
        const engineers = users.filter((u) => u?.role === 'MRO_ENGINEER' && u?.isActive !== false);
        if (!ignore) setMroEngineers(engineers);
      } catch {
        if (!ignore && !(Array.isArray(engineersProp) && engineersProp.length)) {
          setMroEngineers([]);
        }
      } finally {
        if (!ignore) setEngineersLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [open, engineersProp]);

  const currentId = idOf(workOrder?.mroCurrentEngineerId || workOrder?.mroAssignedEngineerId);
  const teamLocked = Boolean(workOrder?.mroEngineerTeamLocked);
  const teamIds = useMemo(() => {
    const fromArray = Array.isArray(workOrder?.mroAssignedEngineerIds)
      ? workOrder.mroAssignedEngineerIds.map(idOf)
      : [];
    const legacy = workOrder?.mroAssignedEngineerId ? [idOf(workOrder.mroAssignedEngineerId)] : [];
    return [...new Set([...fromArray, ...legacy].filter(Boolean))];
  }, [workOrder]);

  const candidates = useMemo(() => {
    const pool = (teamLocked && teamIds.length > 1)
      ? mroEngineers.filter((e) => teamIds.includes(String(e._id)))
      : mroEngineers;
    return pool.filter((e) => String(e._id) !== currentId);
  }, [mroEngineers, teamIds, currentId, teamLocked]);

  useEffect(() => {
    if (!open) return;
    setSelectedCheckIds(incompleteChecks.map((check) => String(check._id)));
    setNotes('');
    setError('');
    setHandoverMode('ASSIGN');
    setSelectedEngineer(null);
  }, [open, workOrder?._id]); // eslint-disable-line react-hooks/exhaustive-deps -- reset form when dialog opens for a WO

  useEffect(() => {
    if (!open || handoverMode !== 'ASSIGN') return;
    setSelectedEngineer((prev) => {
      if (prev && candidates.some((c) => String(c._id) === String(prev._id))) return prev;
      return null;
    });
  }, [open, candidates, handoverMode]);

  const handleClose = () => {
    setSelectedEngineer(null);
    setNotes('');
    setSelectedCheckIds([]);
    setError('');
    setHandoverMode('ASSIGN');
    onClose();
  };

  const toggleCheck = (checkId) => {
    setSelectedCheckIds((prev) => (
      prev.includes(checkId)
        ? prev.filter((id) => id !== checkId)
        : [...prev, checkId]
    ));
  };

  const handleSubmit = async () => {
    const releaseToPool = handoverMode === 'RELEASE';
    if (!releaseToPool && !selectedEngineer?._id) {
      setError('Select the next shift engineer, or choose release without assigning');
      return;
    }
    if (!notes.trim()) {
      setError('Handover notes are required');
      return;
    }
    if (incompleteChecks.length > 0 && selectedCheckIds.length === 0) {
      setError('Select at least one incomplete checklist item to hand over, or complete them first');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const endpoint = useFacade
        ? `/api/mro/${workOrder._id}/handover`
        : `/api/maintenance/${workOrder._id}/mro/handover`;
      const payload = {
        notes: notes.trim(),
        releaseToPool
      };
      if (!releaseToPool) {
        payload.toEngineerId = selectedEngineer._id;
      }
      if (incompleteChecks.length > 0) {
        payload.ampCheckIds = selectedCheckIds;
      }
      await axios.post(endpoint, payload);
      showToast(
        releaseToPool
          ? 'Work order released to the open engineer pool'
          : (hasChecks && selectedCheckIds.length
            ? 'Work order handed over at checklist to next shift engineer'
            : 'Work order handed over to next shift engineer'),
        'success'
      );
      onSuccess?.();
      handleClose();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to hand over work order';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const currentEngineer = mroEngineers.find((e) => String(e._id) === currentId)
    || workOrder?.mroCurrentEngineerId
    || workOrder?.mroAssignedEngineerId;

  const canSubmit = Boolean(notes.trim())
    && !(incompleteChecks.length > 0 && selectedCheckIds.length === 0)
    && (handoverMode === 'RELEASE' || Boolean(selectedEngineer?._id));

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {hasChecks && incompleteChecks.length > 0
          ? 'Hand over at checklist'
          : 'Hand over to next shift'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title || workOrder?.workOrderNumber}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current engineer: <strong>{engineerLabel(currentEngineer) || 'Unassigned'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {teamLocked ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            Manager locked this job to an assigned engineer team. Pick the next engineer from that team, or release to the open pool.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            Search and pick any MRO engineer, or release the job without assigning so any engineer can pick it up.
          </Alert>
        )}

        {hasChecks && (
          <Box sx={{ mb: 2 }}>
            {incompleteChecks.length === 0 ? (
              <Alert severity="success">
                All checklist items are completed. Handover will transfer shift ownership only.
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Incomplete checklist items ({selectedCheckIds.length}/{incompleteChecks.length} selected)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Selected items receive your handover note for the next engineer.
                </Typography>
                <FormGroup sx={{ maxHeight: 220, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1 }}>
                  {incompleteChecks.map((check) => {
                    const checkId = String(check._id);
                    return (
                      <FormControlLabel
                        key={checkId}
                        control={(
                          <Checkbox
                            checked={selectedCheckIds.includes(checkId)}
                            onChange={() => toggleCheck(checkId)}
                            size="small"
                          />
                        )}
                        label={(
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                            <Typography variant="body2">{checkLabel(check)}</Typography>
                            <Chip
                              size="small"
                              label={check.status || 'PENDING'}
                              color={check.status === 'IN_PROGRESS' ? 'warning' : 'default'}
                            />
                          </Stack>
                        )}
                      />
                    );
                  })}
                </FormGroup>
              </>
            )}
          </Box>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <FormLabel component="legend">Handover to</FormLabel>
          <RadioGroup
            value={handoverMode}
            onChange={(e) => {
              setHandoverMode(e.target.value);
              setError('');
              if (e.target.value === 'RELEASE') setSelectedEngineer(null);
            }}
          >
            <FormControlLabel
              value="ASSIGN"
              control={<Radio size="small" />}
              label="Assign next MRO engineer"
            />
            <FormControlLabel
              value="RELEASE"
              control={<Radio size="small" />}
              label="Release without assigning (open pool)"
            />
          </RadioGroup>
        </FormControl>

        {handoverMode === 'ASSIGN' ? (
          <Box sx={{ mb: 2 }}>
            <Autocomplete
              options={candidates}
              loading={engineersLoading}
              value={selectedEngineer}
              onChange={(_event, value) => setSelectedEngineer(value)}
              getOptionLabel={(option) => engineerLabel(option)}
              isOptionEqualToValue={(option, value) => String(option?._id) === String(value?._id)}
              filterOptions={(options, state) => {
                const q = String(state.inputValue || '').trim().toLowerCase();
                if (!q) return options;
                return options.filter((eng) => {
                  const haystack = [
                    eng.firstName,
                    eng.lastName,
                    eng.email,
                    engineerLabel(eng)
                  ].filter(Boolean).join(' ').toLowerCase();
                  return haystack.includes(q);
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search MRO engineers"
                  placeholder="Type name or email..."
                  helperText={
                    candidates.length
                      ? `${candidates.length} engineer${candidates.length === 1 ? '' : 's'} available`
                      : (teamLocked
                        ? 'No other engineers on the locked team. Release to pool, or ask a manager to update the team.'
                        : 'No other MRO engineers found. You can still release without assigning.')
                  }
                />
              )}
              noOptionsText={engineersLoading ? 'Loading engineers...' : 'No matching MRO engineers'}
            />
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ownership will be cleared. Any MRO engineer can pick up this work order. Your handover note stays on the selected checklist items.
          </Alert>
        )}

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Handover notes (Required)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={
            hasChecks && incompleteChecks.length > 0
              ? 'What was done on selected checks, open items, tools/parts status, safety notes...'
              : 'What was done, open items, tools/parts status, safety notes...'
          }
          required
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
        >
          {loading
            ? 'Handing over...'
            : (handoverMode === 'RELEASE'
              ? 'Release to pool'
              : (hasChecks && incompleteChecks.length > 0 ? 'Hand over at checks' : 'Hand over'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MROHandoverDialog;
